/**
 * Pending Invites List Component
 *
 * Displays:
 * - List of pending invitations (admin only)
 * - Email address
 * - Expiration date
 * - Cancel button
 *
 * Presentational component: No business logic, only displays data
 */
import React from 'react';
import { View, Text, Pressable } from 'react-native';
import type { TFunction } from '@dramadetector/shared';
import { useTranslation } from '@dramadetector/shared';

interface PendingInvite {
  id: string;
  invited_email: string;
  created_at: string;
  expires_at: string;
}

interface PendingInvitesListProps {
  invites: PendingInvite[];
  onCancelInvite: (inviteId: string, email: string, createdAt: string) => void;
  locale: string;
  t: TFunction<"subscription">;
}

export function PendingInvitesList({
  invites,
  onCancelInvite,
  locale,
  t,
}: PendingInvitesListProps) {
  const { t: tCommon } = useTranslation('common');
  if (invites.length === 0) {
    return null;
  }

  return (
    <View className="bg-white p-6 shadow-sm mb-4">
      <Text className="text-lg font-semibold text-gray-900 mb-3">
        {t('family.pendingInvites')} ({invites.length})
      </Text>
      {invites.map((invite) => (
        <View
          key={invite.id}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-2"
        >
          <View className="flex-row justify-between items-center">
            <View className="flex-1">
              <Text className="text-gray-900 font-medium">
                {invite.invited_email}
              </Text>
              <Text className="text-xs text-gray-500 mt-1">
                {t('family.expiresAt', {
                  date: new Date(invite.expires_at).toLocaleDateString(locale),
                })}
              </Text>
            </View>
            <Pressable
              onPress={() => onCancelInvite(invite.id, invite.invited_email, invite.created_at)}
              className="ml-3 px-3 py-2 bg-white border border-gray-300 rounded-lg active:opacity-80"
            >
              <Text className="text-gray-700 text-sm font-medium">
                {tCommon('cancel')}
              </Text>
            </Pressable>
          </View>
        </View>
      ))}
    </View>
  );
}
